import { Dispatch, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import { ALERT_STATUS } from "@/context/alert/types";
import { refreshTableAndUpdateDetails } from "@/shared/functions/functions";
import { useAnalysisProvidersTableStore } from "@/stores/useTableStore";

import { EditIcon, ReplayIcon } from "@dotnet/telematics-design-system";

import useAxiosPrivate from "../../../../../api/hooks/useAxiosPrivate";
import { ConvertedIntegrationsRow } from "../../../../../Pages/Integrations/types";
import {
  ANALYSIS_PROVIDERS_ACTIONS,
  INTEGRATION_STATUS,
  RenderConditionOptions,
  ROUTES
} from "../../../../../shared";
import useResponse from "../../../../../shared/hooks/useResponse";
import { SharedIntegrationsDataType } from "../../../../DetailsSection/types";

const useAnalysisProvidersInlineActions = () => {
  const { getData, postData } = useAxiosPrivate();
  const { handleCommonError, handleResponse } = useResponse();
  const { getTableItemDetails } = useTableItemDetails();
  const { t } = useTranslation();

  const [rowData, setRowData] = useState<SharedIntegrationsDataType | null>(
    null
  );
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);

  const openEditDialog = (data: any) => {
    setRowData(data);
    setIsEditOpen(true);
  };

  const handleEditAnalysisProvider = async (
    currentRowData: ConvertedIntegrationsRow,
    setIsButtonLoading: Dispatch<SetStateAction<boolean>>
  ) => {
    setIsButtonLoading(true);
    try {
      const { data } = await getData(
        `${ROUTES.AnalysisProviders}/${currentRowData.id}`
      );

      openEditDialog(data);
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsButtonLoading(false);
  };

  const handleRetryAnalysisProvider = async (
    currentRowData: ConvertedIntegrationsRow,
    setIsButtonLoading?: Dispatch<SetStateAction<boolean>>
  ) => {
    setIsButtonLoading?.(true);
    try {
      const { id: rowId, status } = currentRowData;

      const params = useAnalysisProvidersTableStore.getState().params;
      const { contains: searchParams, selectedTableItem } = params;

      const { data } = await postData(
        `${ROUTES.AnalysisProviders}/${rowId}/Retry`
      );

      const itemHasOpenedDetails = selectedTableItem === rowId;

      refreshTableAndUpdateDetails({
        valueInSearchField: searchParams,
        resourceId: data.id,
        itemHasOpenedDetails,
        getTableItemDetails,
        handleResponse
      });

      const integrationNameObj = {
        integrationName: currentRowData.name
      };

      if (status === INTEGRATION_STATUS.Interrupted) {
        handleResponse(
          ALERT_STATUS.Info,
          t("Alert##retry interrupted integration success", integrationNameObj)
        );
      } else {
        handleResponse(
          ALERT_STATUS.Success,
          t("Alert##retry integration success", integrationNameObj)
        );
      }
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsButtonLoading?.(false);
  };

  const inlineActions = [
    {
      title: ANALYSIS_PROVIDERS_ACTIONS.Retry,
      icon: <ReplayIcon />,
      hasLoader: true,
      clickAction: handleRetryAnalysisProvider,
      isEnabled: ({ data }: RenderConditionOptions): boolean =>
        data.status === INTEGRATION_STATUS.Failed ||
        data.status === INTEGRATION_STATUS.Interrupted,
      renderConditions: () => true
    },
    {
      title: ANALYSIS_PROVIDERS_ACTIONS.Edit,
      icon: <EditIcon />,
      hasLoader: true,
      clickAction: handleEditAnalysisProvider,
      renderConditions: () => true
    }
  ];

  return {
    inlineActions,
    isEditOpen,
    rowData,
    setRowData
  };
};

export default useAnalysisProvidersInlineActions;
